/**
 * A unica porta de dados do modulo 01.
 *
 * Nenhuma tela chama `fetch` direto e nao ha mais fixture nenhuma no sistema — as
 * duas regras sao cobradas pelo `npm run doctor`. Aqui nao ha fixture nenhuma:
 * este modulo nasceu ligado ao banco, e por isso as telas dele NAO levam o
 * aviso de demonstracao. O que elas mostram e o que esta gravado.
 *
 * Toda escrita invalida a lista do proprio cadastro E a trilha: a trilha e
 * escrita pelo trigger, na mesma transacao, entao ela muda junto sem ninguem
 * pedir — e uma aba de historico que nao se atualiza depois da edicao faz
 * parecer que a auditoria perdeu o registro.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { formatarDataHora, formatarReais } from '@/lib/formato';
import { EFEITO_DA_CONTINGENCIA } from '@/lib/contingencia';
import { retornoDe } from '@/lib/retorno';
import { ehFichaAlterada } from '@/lib/versao-da-ficha';

import { enviarJson, pedir } from './api';
import { NOME_DO_ASSUNTO, NOME_DO_CADASTRO } from './tipos';
import type {
  Alcada,
  LinhaDaTrilha,
  LinhaDeCadastro,
  ConviteEmitido,
  PapelDaPessoa,
  Papel,
  Permissao,
  PermissaoDoPapel,
  Pessoa,
  TipoDeCadastro,
} from './tipos';

interface Lista<T> {
  readonly itens: readonly T[];
}

/**
 * Lista que o servidor CORTA, com o recorte declarado.
 *
 * A trilha volta no maximo 100 linhas por padrao, e a tela escrevia "N
 * registros" com N = o tamanho da resposta. Um auditor lia "100 registros"
 * numa empresa com milhares de alteracoes, sem paginacao e sem aviso de corte.
 * `total` e `limite` sobem junto para a tela conseguir escrever "mostrando as
 * 100 mais recentes, de 4.312" — que e a diferenca entre um numero e um
 * palpite.
 */
export interface ListaCortada<T> {
  readonly itens: readonly T[];
  readonly limite: number;
  readonly total: number;
  /** O cursor da pagina seguinte, ou nulo na ultima (trilha, desde 06/10/2026). */
  readonly proximo?: string | null;
}

const CHAVE = {
  cadastro: (tipo: TipoDeCadastro) => ['cadastros', tipo] as const,
  papeis: ['cadastros', 'papeis'] as const,
  permissoes: ['cadastros', 'permissoes'] as const,
  permissoesDoPapel: (id: string) => ['cadastros', 'papeis', id, 'permissoes'] as const,
  pessoas: ['cadastros', 'usuarios'] as const,
  alcadas: ['cadastros', 'alcadas'] as const,
  trilha: (filtro: FiltroDaTrilha) => ['cadastros', 'trilha', filtro] as const,
};

// ------------------------------------------------------------------ estrutura

/* O retorno de cada gravação (R04, p.32): o particípio concorda com o nome. */
function feito(nome: string, genero: 'm' | 'f', verbo: 'cadastrad' | 'alterad' | 'arquivad'): string {
  return `${nome.charAt(0).toUpperCase()}${nome.slice(1)} ${verbo}${genero === 'f' ? 'a' : 'o'}`;
}

function verboDaGravacao(id: string | null, campos: Record<string, unknown>): 'cadastrad' | 'alterad' | 'arquivad' {
  if (id === null) return 'cadastrad';
  return campos['ativo'] === false ? 'arquivad' : 'alterad';
}

function nomeNoCache<T extends { readonly id: string }>(cliente: QueryClient, chave: readonly unknown[], id: string | null, ler: (x: T) => string): string | undefined {
  const dado = cliente.getQueryData<readonly T[] | { readonly itens: readonly T[] }>(chave);
  const lista = Array.isArray(dado) ? (dado as readonly T[]) : (dado as { readonly itens?: readonly T[] } | undefined)?.itens;
  const achado = lista?.find((x) => x.id === id);
  return achado ? ler(achado) : undefined;
}

/** `ligada` falso nao pede: a janela que so escolhe da lista pergunta quando abre. */
export function useCadastro(tipo: TipoDeCadastro, ligada = true) {
  return useQuery({
    queryKey: CHAVE.cadastro(tipo),
    enabled: ligada,
    queryFn: async () => (await pedir<Lista<LinhaDeCadastro>>(`/cadastros/${tipo}`)).itens,
  });
}

/**
 * As listas que os formularios usam para ESCOLHER, em vez de digitar.
 *
 * Elas sao sempre as mesmas tres — filial, cliente e centro de custo — porque
 * sao as referencias dos seis cadastros. Carregar as tres de uma vez custa tres
 * requisicoes pequenas e evita o campo de digitar uuid, que e como uma
 * referencia acaba apontando para o cadastro errado.
 */
export function useReferencias() {
  const filiais = useCadastro('filiais');
  const clientes = useCadastro('clientes');
  const centros = useCadastro('centros-de-custo');
  return { filiais, clientes, centros };
}

export function useGravarCadastro(tipo: TipoDeCadastro) {
  const cliente = useQueryClient();
  const nome = NOME_DO_CADASTRO[tipo];
  return useMutation({
    meta: {
      retorno: retornoDe<LinhaDeCadastro, { id: string | null; campos: Record<string, unknown> }>((r, v) => ({
        titulo: `${feito(nome.um, nome.genero, verboDaGravacao(v.id, v.campos))}${r.emContingencia ? ' em contingência' : ''}`,
        ...(r.emContingencia ? { tom: 'ressalva' as const } : {}),
        alvo: { codigo: r.codigo, nome: r.nome ?? r.descricao ?? '' },
        fatos: [r.municipio ? `${r.municipio}${r.uf ? `/${r.uf}` : ''}` : null, r.documento ?? null, r.tipo ?? null],
        efeito: r.emContingencia ? EFEITO_DA_CONTINGENCIA : 'A alteração ficou na trilha, com quem mudou e quando',
      })),
    },
    mutationFn: ({ id, campos }: { readonly id: string | null; readonly campos: Record<string, unknown> }) =>
      id === null
        ? enviarJson<LinhaDeCadastro>(`/cadastros/${tipo}`, 'POST', campos)
        : enviarJson<LinhaDeCadastro>(`/cadastros/${tipo}/${id}`, 'PATCH', campos),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['cadastros'] });
    },
    /* Recusada por versao velha (outra pessoa gravou depois que o formulario
       abriu): a lista rele, e reabrir mostra a versao de agora. */
    onError: (erro) => {
      if (ehFichaAlterada(erro)) void cliente.invalidateQueries({ queryKey: ['cadastros'] });
    },
  });
}

// ----------------------------------------------------------------- governanca

export function usePapeis() {
  return useQuery({
    queryKey: CHAVE.papeis,
    queryFn: async () => (await pedir<Lista<Papel>>('/cadastros/papeis')).itens,
  });
}

export function usePermissoes() {
  return useQuery({
    queryKey: CHAVE.permissoes,
    queryFn: async () => (await pedir<Lista<Permissao>>('/cadastros/permissoes')).itens,
  });
}

export function usePermissoesDoPapel(papelId: string | null) {
  return useQuery({
    queryKey: CHAVE.permissoesDoPapel(papelId ?? ''),
    enabled: papelId !== null,
    queryFn: async () =>
      (await pedir<Lista<PermissaoDoPapel>>(`/cadastros/papeis/${papelId ?? ''}/permissoes`)).itens,
  });
}

/** `ligada` como em `useCadastro`: a lista exige governanca.ler. */
export function usePessoas(ligada = true) {
  return useQuery({
    queryKey: CHAVE.pessoas,
    enabled: ligada,
    queryFn: () => pedir<{ itens: readonly Pessoa[]; papeis: readonly PapelDaPessoa[] }>('/cadastros/usuarios'),
  });
}

export function useAlcadas() {
  return useQuery({
    queryKey: CHAVE.alcadas,
    queryFn: async () => (await pedir<Lista<Alcada>>('/cadastros/alcadas')).itens,
  });
}

export function useGravarPapel() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<Papel, { id: string | null; campos: Record<string, unknown> }>((r, v) => ({
        titulo: feito('papel', 'm', verboDaGravacao(v.id, v.campos)),
        alvo: { codigo: r.codigo, nome: r.nome },
        fatos: [`${String(r.permissoes)} permissões`, `${String(r.pessoas)} pessoas`],
      })),
    },
    mutationFn: ({ id, campos }: { readonly id: string | null; readonly campos: Record<string, unknown> }) =>
      id === null
        ? enviarJson<Papel>('/cadastros/papeis', 'POST', campos)
        : enviarJson<Papel>(`/cadastros/papeis/${id}`, 'PATCH', campos),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['cadastros'] });
    },
    /* Recusada por versao velha (outra pessoa gravou depois que o formulario
       abriu): a lista rele, e reabrir mostra a versao de agora. */
    onError: (erro) => {
      if (ehFichaAlterada(erro)) void cliente.invalidateQueries({ queryKey: ['cadastros'] });
    },
  });
}

/**
 * 0128 · Gravar a trava do lançamento de abastecimento do papel. O corpo leva
 * a versão do papel aberto (409 se outra pessoa gravou depois) e o motivo, que
 * vai para a trilha com o autor.
 */
export interface PedidoDaTrava {
  readonly tipos: readonly ('interno' | 'externo')[] | null;
  readonly tanques: readonly string[] | null;
  readonly versao: string | null | undefined;
  readonly motivo: string;
  /** Só para o toast: a trava escrita ("Só interno · 1 tanque"). Não vai no corpo. */
  readonly resumo: string;
}

export function useGravarTravaDoPapel(papel: Pick<Papel, 'id' | 'codigo' | 'nome'>) {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<Papel, PedidoDaTrava>((_r, v) => ({
        titulo: 'Trava do lançamento gravada',
        alvo: { codigo: papel.codigo, nome: papel.nome },
        fatos: [v.resumo, `motivo: ${v.motivo}`],
        efeito: 'Vale no próximo lançamento de quem tem o papel',
      })),
    },
    mutationFn: (v: PedidoDaTrava) =>
      enviarJson<Papel>(`/cadastros/papeis/${papel.id}/trava-de-abastecimento`, 'PUT', {
        tipos: v.tipos,
        tanques: v.tanques,
        ...(v.versao ? { versao: v.versao } : {}),
        motivo: v.motivo,
      }),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['cadastros'] });
    },
    onError: (erro) => {
      if (ehFichaAlterada(erro)) void cliente.invalidateQueries({ queryKey: ['cadastros'] });
    },
  });
}

/** 0130 · O pedido do início do app do papel. */
export interface PedidoDoInicio {
  readonly inicio: 'padrao' | 'abastecimento';
  readonly versao: string | null | undefined;
  readonly motivo: string;
}

export function useGravarInicioDoPapel(papel: Pick<Papel, 'id' | 'codigo' | 'nome'>) {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<Papel, PedidoDoInicio>((_r, v) => ({
        titulo: 'Início do app gravado',
        alvo: { codigo: papel.codigo, nome: papel.nome },
        fatos: [v.inicio === 'abastecimento' ? 'Abastecimento' : 'Painel', `motivo: ${v.motivo}`],
        efeito: 'Vale quando a pessoa abrir o app de novo',
      })),
    },
    mutationFn: (v: PedidoDoInicio) =>
      enviarJson<Papel>(`/cadastros/papeis/${papel.id}/inicio-do-app`, 'PUT', {
        inicio: v.inicio,
        ...(v.versao ? { versao: v.versao } : {}),
        motivo: v.motivo,
      }),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['cadastros'] });
    },
    onError: (erro) => {
      if (ehFichaAlterada(erro)) void cliente.invalidateQueries({ queryKey: ['cadastros'] });
    },
  });
}

/**
 * 0132 · DUPLICAR O PAPEL (decisão 58): a cópia leva as permissões, a trava, o
 * início do app e, no parceiro, o fornecedor escolhido aqui. O código nasce do
 * nome, no servidor. O retorno nomeia o papel novo e o código dele.
 */
export interface PedidoDaCopia {
  readonly nome: string;
  /** Só no papel de parceiro: o fornecedor da cópia, ou nulo para escolher na concessão. */
  readonly fornecedorId?: string | null;
  readonly motivo: string;
}

export function useDuplicarPapel(papel: Pick<Papel, 'id' | 'codigo' | 'nome'>) {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<Papel, PedidoDaCopia>((r) => ({
        titulo: 'Papel duplicado',
        alvo: { codigo: r.codigo, nome: r.nome },
        fatos: [`cópia de ${papel.nome}`, `${String(r.permissoes)} ${r.permissoes === 1 ? 'permissão' : 'permissões'}`],
        efeito: 'Ele já pode ser concedido às pessoas',
      })),
    },
    mutationFn: (v: PedidoDaCopia) =>
      enviarJson<Papel>(`/cadastros/papeis/${papel.id}/duplicacao`, 'POST', {
        nome: v.nome,
        ...(v.fornecedorId !== undefined ? { fornecedorId: v.fornecedorId } : {}),
        motivo: v.motivo,
      }),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['cadastros'] });
    },
  });
}

/** 0132 · O fornecedor fixo do papel de parceiro: o de UM posto, ou nulo para escolher na concessão. */
export interface PedidoDoFornecedor {
  readonly fornecedorId: string | null;
  readonly versao: string | null | undefined;
  readonly motivo: string;
  /** Só para o toast: o nome do fornecedor escolhido. Não vai no corpo. */
  readonly nome: string;
}

export function useFixarFornecedorDoPapel(papel: Pick<Papel, 'id' | 'codigo' | 'nome'>) {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<Papel, PedidoDoFornecedor>((_r, v) => ({
        titulo: 'Fornecedor do papel gravado',
        alvo: { codigo: papel.codigo, nome: papel.nome },
        fatos: [v.nome, `motivo: ${v.motivo}`],
        efeito: 'Vale nas próximas concessões do papel',
      })),
    },
    mutationFn: (v: PedidoDoFornecedor) =>
      enviarJson<Papel>(`/cadastros/papeis/${papel.id}/fornecedor`, 'PUT', {
        fornecedorId: v.fornecedorId,
        ...(v.versao ? { versao: v.versao } : {}),
        motivo: v.motivo,
      }),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['cadastros'] });
    },
    onError: (erro) => {
      if (ehFichaAlterada(erro)) void cliente.invalidateQueries({ queryKey: ['cadastros'] });
    },
  });
}

export function useCriarPermissao() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<Permissao>((r) => ({
        titulo: 'Permissão cadastrada',
        alvo: { codigo: r.codigo, nome: r.descricao },
        fatos: [`módulo ${r.modulo}`],
        efeito: 'Já pode ser concedida a um papel',
      })),
    },
    mutationFn: (campos: Record<string, unknown>) =>
      enviarJson<Permissao>('/cadastros/permissoes', 'POST', campos),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['cadastros'] });
    },
  });
}

export function useVincularPermissao(papelId: string | null) {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, { permissaoIds: readonly string[]; conceder: boolean; motivo?: string }>((_r, v) => {
        const papel = nomeNoCache<Papel>(cliente, CHAVE.papeis, papelId, (p) => p.nome);
        /* O TOAST NOMEIA quando é uma, e CONTA quando são várias: listar
           catorze códigos num toast é um toast que ninguém lê. */
        const codigos = v.permissaoIds
          .map((id) => nomeNoCache<Permissao>(cliente, CHAVE.permissoes, id, (p) => p.codigo))
          .filter((c): c is string => c !== null && c !== undefined);
        const quantas = v.permissaoIds.length;
        const oQue =
          quantas === 1 ? (codigos[0] ?? null) : `${String(quantas)} permissões: ${codigos.slice(0, 3).join(', ')}…`;
        return {
          titulo: v.conceder
            ? quantas === 1
              ? 'Permissão concedida'
              : 'Permissões concedidas'
            : 'Permissão retirada',
          ...(papel ? { alvo: { nome: `papel ${papel}` } } : {}),
          fatos: [oQue, v.motivo ? `motivo: ${v.motivo}` : null],
          efeito: 'Vale no próximo pedido de quem tem o papel',
        };
      }),
    },
    /*
     * CONCEDER É EM LOTE; TIRAR CONTINUA UMA A UMA — e a assimetria é do
     * domínio, não de preguiça. Conceder é montar um papel, e montar é um
     * ato só com muitas partes. Tirar é desfazer uma decisão: cada retirada
     * pede motivo próprio (`papel.tirar_permissao`), e um motivo que
     * explicasse catorze retiradas de uma vez não explicaria nenhuma.
     */
    mutationFn: ({
      permissaoIds,
      conceder,
      motivo,
    }: {
      readonly permissaoIds: readonly string[];
      readonly conceder: boolean;
      readonly motivo?: string;
    }) =>
      conceder
        ? enviarJson<unknown>(`/cadastros/papeis/${papelId ?? ''}/permissoes`, 'POST', {
            permissaoIds,
            ...(motivo ? { motivo } : {}),
          })
        : enviarJson<unknown>(
            `/cadastros/papeis/${papelId ?? ''}/permissoes/${permissaoIds[0] ?? ''}`,
            'DELETE',
            motivo ? { motivo } : {},
          ),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['cadastros'] });
    },
  });
}

export function useGravarAlcada() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<Alcada>((r) => ({
        titulo: 'Alçada gravada',
        alvo: { nome: `papel ${r.papel_nome}` },
        fatos: [
          NOME_DO_ASSUNTO[r.assunto],
          r.limite_valor === null ? 'sem teto' : `até ${formatarReais(Number(r.limite_valor))}`,
          r.exige_segundo_aprovador ? 'exige segundo aprovador' : null,
        ],
      })),
    },
    mutationFn: (campos: Record<string, unknown>) => enviarJson<Alcada>('/cadastros/alcadas', 'POST', campos),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['cadastros'] });
    },
  });
}

export function useRemoverAlcada() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, { id: string; motivo?: string }>((_r, v) => {
        const alcada = cliente.getQueryData<readonly Alcada[]>(CHAVE.alcadas)?.find((a) => a.id === v.id);
        return {
          titulo: 'Alçada removida',
          ...(alcada ? { alvo: { nome: `papel ${alcada.papel_nome}` } } : {}),
          fatos: [alcada ? NOME_DO_ASSUNTO[alcada.assunto] : null, v.motivo ? `motivo: ${v.motivo}` : null],
        };
      }),
    },
    mutationFn: ({ id, motivo }: { readonly id: string; readonly motivo?: string }) =>
      enviarJson<{ removidas: number }>(`/cadastros/alcadas/${id}`, 'DELETE', motivo ? { motivo } : {}),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['cadastros'] });
    },
  });
}

/**
 * Conceder e tirar papel. Desde a 0098 o mesmo papel vale em mais de um
 * alcance, e por isso a retirada e por VINCULO (`vinculoId`): pelo papel, ela
 * levaria todos os alcances de uma vez.
 */
export function useVincularPapel(pessoaId: string | null) {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, PedidoDeVinculo>((_r, v) => {
        const pessoa = nomeNoCache<Pessoa>(cliente, CHAVE.pessoas, pessoaId, (p) => p.nome);
        const papel = nomeNoCache<Papel>(cliente, CHAVE.papeis, v.papelId, (p) => p.nome);
        return {
          titulo: v.conceder ? 'Papel concedido' : 'Papel retirado',
          ...(pessoa ? { alvo: { nome: pessoa } } : {}),
          fatos: [papel ?? null, v.alcance ?? null, v.motivo ? `motivo: ${v.motivo}` : null],
          efeito: 'Vale no próximo pedido da pessoa',
        };
      }),
    },
    mutationFn: (v: PedidoDeVinculo) =>
      v.conceder
        ? enviarJson<unknown>(`/cadastros/usuarios/${pessoaId ?? ''}/papeis`, 'POST', {
            papelId: v.papelId,
            /* Um alcance so (0081, 0083, 0098, 0123): o servidor recusa dois. */
            ...(v.fornecedorId
              ? { fornecedorId: v.fornecedorId }
              : v.contratoId
                ? { contratoId: v.contratoId }
                : v.obraId
                  ? { obraId: v.obraId }
                  : v.filialId
                    ? { filialId: v.filialId }
                    : {}),
            ...(v.inicio ? { inicio: v.inicio } : {}),
            ...(v.fim ? { fim: v.fim } : {}),
            ...(v.motivo ? { motivo: v.motivo } : {}),
          })
        : v.vinculoId
          ? enviarJson<unknown>(
              `/cadastros/usuarios/${pessoaId ?? ''}/vinculos/${v.vinculoId}`,
              'DELETE',
              v.motivo ? { motivo: v.motivo } : {},
            )
          : enviarJson<unknown>(`/cadastros/usuarios/${pessoaId ?? ''}/papeis/${v.papelId}`, 'DELETE', v.motivo ? { motivo: v.motivo } : {}),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['cadastros'] });
    },
  });
}

export interface PedidoDeVinculo {
  readonly papelId: string;
  readonly conceder: boolean;
  readonly motivo?: string;
  /** O alcance (0081, 0083, 0098): um só; nenhum, e o papel vale na empresa inteira. */
  readonly filialId?: string | null;
  readonly obraId?: string | null;
  readonly contratoId?: string | null;
  /** 0123 · só o Posto parceiro, e nele é obrigatório: o servidor recusa sem. */
  readonly fornecedorId?: string | null;
  /** A vigência (0083), `aaaa-mm-dd`. Sem início, vale desde hoje. */
  readonly inicio?: string | null;
  readonly fim?: string | null;
  /** Na retirada: o vínculo (um alcance do papel), e não o papel inteiro. */
  readonly vinculoId?: string;
  /** Só para o toast: o alcance escrito ("Só no contrato CTR-2026-01"). */
  readonly alcance?: string;
}

export function useGravarPessoa() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<Pessoa, { campos: Record<string, unknown> }>((r, v) => ({
        titulo: v.campos['ativo'] === false ? 'Pessoa desativada' : v.campos['ativo'] === true ? 'Pessoa reativada' : 'Pessoa alterada',
        alvo: { nome: r.nome },
        fatos: [r.email, r.filial_nome],
      })),
    },
    mutationFn: ({ id, campos }: { readonly id: string; readonly campos: Record<string, unknown> }) =>
      enviarJson<Pessoa>(`/cadastros/usuarios/${id}`, 'PATCH', campos),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['cadastros'] });
    },
    /* Recusada por versao velha (outra pessoa gravou depois que o formulario
       abriu): a lista rele, e reabrir mostra a versao de agora. */
    onError: (erro) => {
      if (ehFichaAlterada(erro)) void cliente.invalidateQueries({ queryKey: ['cadastros'] });
    },
  });
}

// --------------------------------------------------------------------- trilha

export interface FiltroDaTrilha {
  readonly recurso?: string;
  readonly recursoId?: string;
  readonly acao?: string;
  readonly limite?: number;
}

function comoBusca(filtro: FiltroDaTrilha): string {
  const partes = new URLSearchParams();
  if (filtro.recurso) partes.set('recurso', filtro.recurso);
  if (filtro.recursoId) partes.set('recursoId', filtro.recursoId);
  if (filtro.acao) partes.set('acao', filtro.acao);
  if (filtro.limite) partes.set('limite', String(filtro.limite));
  const texto = partes.toString();
  return texto ? `?${texto}` : '';
}

/**
 * A trilha em paginas, pelo cursor do servidor (ocorrido_em, id): "Carregar
 * mais" pede as linhas anteriores a ultima que chegou. A chave fica sob
 * `['cadastros', 'trilha', ...]`, entao toda escrita que invalida a trilha
 * invalida esta tambem — e ela volta a primeira pagina.
 */
/* `ligada` SEM PADRAO (robôs no QAS, 20/10/2026): o painel do cadastro pedia a
   trilha a quem nao le auditoria — Compras gravava o fornecedor e recebia 403 e
   "peça Controladoria". Cada chamada decide pela licença `auditoria.ler`. */
export function useTrilhaPaginada(filtro: FiltroDaTrilha, ligada: boolean) {
  const consulta = useInfiniteQuery({
    queryKey: [...CHAVE.trilha(filtro), 'paginas'] as const,
    enabled: ligada,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const busca = comoBusca(filtro);
      const cursor = pageParam ? `${busca ? '&' : '?'}depoisDe=${encodeURIComponent(pageParam)}` : '';
      return pedir<ListaCortada<LinhaDaTrilha>>(`/cadastros/trilha${busca}${cursor}`);
    },
    getNextPageParam: (ultima) => ultima.proximo ?? null,
  });
  const paginas = consulta.data?.pages ?? [];
  const primeira = paginas[0];
  return {
    consulta,
    itens: paginas.flatMap((p) => p.itens),
    total: primeira?.total ?? 0,
    carregada: primeira !== undefined,
    temMais: consulta.hasNextPage,
    carregandoMais: consulta.isFetchingNextPage,
    carregarMais: () => {
      void consulta.fetchNextPage();
    },
  };
}

export function useTrilha(filtro: FiltroDaTrilha, ligada: boolean) {
  return useQuery({
    queryKey: CHAVE.trilha(filtro),
    enabled: ligada,
    queryFn: () => pedir<ListaCortada<LinhaDaTrilha>>(`/cadastros/trilha${comoBusca(filtro)}`),
  });
}

/**
 * Criar pessoa.
 *
 * A rota nao existia, e a tela justificava a ausencia por um bloqueio que ja
 * tinha sido removido (a migracao 0009 deu a `org.usuario.empresa_id` o default
 * vindo do contexto). A ferramenta "Perfis e aprovacoes" so operava sobre as
 * cinco pessoas da semente.
 *
 * O corpo NAO carrega empresa: ela vem do token, e a coluna a tira do contexto
 * da transacao. Mandar `empresa_id` aqui seria 400 do outro lado.
 */
/** Emite o convite de primeiro acesso ou de troca de senha (migração 0024). */
/**
 * Como o convite chega à pessoa neste ambiente (0097): por e-mail, quando o
 * servidor tem o envio configurado, ou pela mão de quem administra. Só o
 * sim/não — a tela nunca vê servidor nem remetente.
 */
export function useEntregaDoConvite(ligado = true) {
  return useQuery({
    queryKey: ['cadastros', 'convites', 'entrega'] as const,
    enabled: ligado,
    queryFn: () => pedir<{ readonly porEmail: boolean }>('/cadastros/convites/entrega'),
    staleTime: 5 * 60 * 1000,
  });
}

/** O fato da entrega no retorno da emissão (R04): para onde foi, ou que é à mão. */
export function fatoDaEntrega(entrega: ConviteEmitido['entrega'] | undefined, email: string | null | undefined): string {
  return entrega === 'email' ? `por e-mail para ${email ?? 'a pessoa'}` : 'entrega à mão';
}

export function useEmitirConvite(pessoaId: string) {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<ConviteEmitido>((r) => {
        const pessoa = nomeNoCache<Pessoa>(cliente, CHAVE.pessoas, pessoaId, (p) => p.nome);
        const email = nomeNoCache<Pessoa>(cliente, CHAVE.pessoas, pessoaId, (p) => p.email ?? '');
        return {
          titulo: 'Convite emitido',
          ...(pessoa ? { alvo: { nome: pessoa } } : {}),
          fatos: [`vale até ${formatarDataHora(new Date(r.expiraEm))}`, fatoDaEntrega(r.entrega, email || null)],
          efeito:
            r.entrega === 'email'
              ? 'O e-mail sai em instantes; o convite anterior, se havia, deixou de valer'
              : 'O convite anterior, se havia, deixou de valer',
        };
      }),
    },
    mutationFn: () =>
      enviarJson<ConviteEmitido>(`/cadastros/usuarios/${pessoaId}/convite`, 'POST', { motivo: 'convite emitido pela tela de governança' }),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['cadastros'] });
    },
  });
}

export function useCriarPessoa() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<Pessoa>((r) => ({
        titulo: 'Pessoa cadastrada',
        alvo: { nome: r.nome },
        fatos: [r.email, r.filial_nome],
        ...(r.convite_ate
          ? { efeito: `Convite de primeiro acesso vale até ${formatarDataHora(new Date(r.convite_ate))}` }
          : r.tem_acesso
            ? {}
            : { efeito: 'Sem acesso até alguém emitir o convite' }),
      })),
    },
    mutationFn: (campos: Record<string, unknown>) => enviarJson<Pessoa>('/cadastros/usuarios', 'POST', campos),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['cadastros'] });
    },
  });
}
